import { replaceAtIndex } from '../libs/helpers';
import {
  CONVERSATION_CREATED,
  CONVERSATION_READ_ONE,
  CONVERSATION_READ_ALL,
  MESSAGE_CREATED,
  MESSAGES_READ,
  MESSAGE_MARKED_READ,
  UNREAD_COUNT_RECEIVED,
  ARCHIVED_COUNT_RECEIVED,
  TOP_MESSAGES_READ,
  RESET_MESSAGES,
  UNREAD_COUNT_RECEIVED_QUOTES,
  CONVERSATION_READ_ALL_UNREAD,
  CONVERSATION_READ_ALL_ARCHIVED,
  CONVERSATION_READ_ALL_SUPPLIER_V2,
  MESSAGE_CREATED_V1,
} from '../constants/actions.realtime';
import { QUOTE_CONVERSATION_MAPPED } from '../constants/action-types';

const initialState = {
  conversationId: null,
  threads: [],
  messages: [],
  topMessages: [],
  unreadCount: 0,
  quoteConversationMap: {},
  totals: {
    inbox: 0,
    unread: 0,
    archived: 0,
    supplier: 0,
  },
};

const noop = () => ({});

const socketReducer = ({
  key = '',
  onSend = noop,
  onReceive = noop,
  onError = noop,
}) => {
  const loadingKey = `loading${key}`;
  const errorKey = `error${key}`;

  return (state, action) => {
    if (!action.__FROM_SOCKET__) {
      return {
        ...state,
        ...onSend(state, action),
        [loadingKey]: true,
        [errorKey]: null,
      };
    }

    if (action.data.error) {
      return {
        ...state,
        ...onError(state, action),
        [loadingKey]: false,
        [errorKey]: action.data.error,
      };
    }

    return {
      ...state,
      ...onReceive(state, action),
      [loadingKey]: false,
      [errorKey]: null,
    };
  };
};

const onCreateConversation = socketReducer({
  key: 'CreateConversation',
  onReceive: (state, { data }) => ({
    threads: [...state.threads, data],
  }),
});

const onReadOneConversation = socketReducer({
  key: 'Conversation',
  onReceive: (state, { data }) => {
    const existingThreadIndex = state.threads.findIndex(
      ({ uuid }) => uuid === data.uuid
    );

    return {
      threads:
        existingThreadIndex > -1
          ? replaceAtIndex(state.threads, existingThreadIndex, {
              ...state.threads[existingThreadIndex],
              ...data,
            })
          : [...state.threads, data],
    };
  },
});

const onAllInboxConversations = socketReducer({
  key: 'ConversationList',
  onReceive: (state, { data }) => ({
    threads: data.threads,
    totals: {
      ...state.totals,
      inbox: data.total,
    },
    // Keep legacy total for backward compatibility
    total: data.total,
  }),
});

const onAllUnreadConversations = socketReducer({
  key: 'UnreadConversations',
  onReceive: (state, { data }) => ({
    threads: data.threads,
    totals: {
      ...state.totals,
      unread: data.total,
    },
    // Keep legacy total for backward compatibility
    total: data.total,
  }),
});

const onAllArchivedConversations = socketReducer({
  key: 'ArchivedConversations',
  onReceive: (state, { data }) => ({
    threads: data.threads,
    totals: {
      ...state.totals,
      archived: data.total,
    },
    // Keep legacy total for backward compatibility
    total: data.total,
  }),
});

const onAllSupplierConversations = socketReducer({
  key: 'SupplierAllMessages',
  onReceive: (state, { data }) => ({
    threads: data.threads,
    totals: {
      ...state.totals,
      supplier: data.total,
    },
    // Keep legacy total for backward compatibility
    total: data.total,
  }),
});

const onMessageCreated = socketReducer({
  key: 'CreateMessage',
  onReceive: (state, { data }) => {
    const existingMessageIndex = state.messages.findIndex(
      ({ uuid }) => uuid === data.uuid
    );

    const createdDate = new Date(data.created);

    // Check if this is a new message (not already in state)
    const isNewMessage = existingMessageIndex === -1;

    // Find the thread in current view
    const threadInCurrentView = state.threads.find(
      ({ uuid }) => uuid === data.conversationId
    );

    // Always update the thread if it exists in current view and this is a new message
    // This ensures real-time updates for incoming messages
    const shouldUpdateThread = threadInCurrentView && isNewMessage;

    // Find the thread index for updating
    const threadIndex = state.threads.findIndex(
      ({ uuid }) => uuid === data.conversationId
    );

    // Determine if we should increment counters:
    // - For new incoming messages only
    // - Only if thread is NOT in current view (to avoid double counting)
    const shouldIncrementCounters =
      isNewMessage && !data.mine && !threadInCurrentView;

    return {
      // Update threads if the thread exists in current view and it's a new message
      ...(shouldUpdateThread && {
        threads: state.threads.map((thread) => {
          if (thread.uuid !== data.conversationId) {
            return thread;
          }

          // Create a completely new latestMessage object to ensure selector re-computation
          const newLatestMessage = {
            uuid: data.uuid,
            content: data.content,
            type: data.type || 'regular',
            created: data.created,
            // Ensure sender structure is preserved
            sender: data.sender ||
              thread.latestMessage?.sender || { id: null, type: null },
            // For new incoming messages, reset readBy to empty array (unread)
            readBy: data.mine ? thread.latestMessage?.readBy || [] : [],
            mine: data.mine || false,
            impersonatingUser: data.impersonatingUser || null,
          };

          // Return a completely new thread object
          return {
            ...thread,
            latestMessage: newLatestMessage,
            // Also update unread count for the thread if it's an incoming message
            unreadMessagesCount: data.mine
              ? thread.unreadMessagesCount
              : (thread.unreadMessagesCount || 0) + 1,
          };
        }),
      }),
      messages:
        existingMessageIndex > -1
          ? replaceAtIndex(state.messages, existingMessageIndex, {
              ...state.messages[existingMessageIndex],
              ...data,
            })
          : [...state.messages, data],
      // Increment tab totals for new incoming messages
      ...(shouldIncrementCounters && {
        totals: {
          ...state.totals,
          // Increment all relevant tab totals for new incoming messages
          inbox: state.totals.inbox + 1,
          supplier: state.totals.supplier + 1,
          // Also increment unread count since new incoming messages are unread
          unread: state.totals.unread + 1,
        },
      }),
    };
  },
});

const onMessagesRead = socketReducer({
  key: 'Messages',
  onSend: (state, { payload: { conversationId, page } }) => {
    if (!page) {
      return {
        conversationId,
        messages: [],
      };
    }

    return {};
  },
  onReceive: (state, { data }) => {
    if (data.conversationId !== state.conversationId) {
      return {};
    }

    return {
      messages: data.messages.concat(state.messages).reduce((last, message) => {
        if (last.some(({ uuid }) => uuid === message.uuid)) {
          return last;
        }

        return [...last, message];
      }, []),
    };
  },
});

const onMessageMarkedRead = socketReducer({
  key: 'ReadMessage',
  onSend: (state, { payload }) => ({
    messages: replaceAtIndex(
      state.messages,
      state.messages.findIndex(({ uuid }) => uuid === payload.messageId),
      (message) => ({
        ...message,
        markedRead: true,
      })
    ),
  }),
  onReceive: (state, { data }) => ({
    messages: replaceAtIndex(
      state.messages,
      state.messages.findIndex(({ uuid }) => uuid === data.messageId),
      ({ markedRead, ...message }) => ({
        ...message,
        readBy: message.readBy
          .filter(({ id }) => id !== data.recipient.id)
          .concat([data.recipient]),
      })
    ),
    unreadCount: Math.max(0, state.unreadCount - 1),
  }),
});

const onUnreadCount = (state, { data: unreadCount }) => ({
  ...state,
  // Guard against non-numeric socket payloads (e.g. an `{ error, errorType,
  // status }` object): the count is rendered directly in the header, and an
  // object child crashes React. Ignore anything that isn't a number.
  unreadCount:
    typeof unreadCount === 'number' ? unreadCount : state.unreadCount,
});

const onArchivedCount = (state, { data: archivedCount }) => ({
  ...state,
  totals: {
    ...state.totals,
    archived: archivedCount,
  },
});

const onUnreadCountQuotes = (state, { data: { unreadQuotes } }) => ({
  ...state,
  unreadQuotes,
});

const onTopMessages = socketReducer({
  key: 'TopMessages',
  onReceive: (state, { data }) => ({
    topMessages: data,
  }),
});

const onQuoteConversationMapped = (state, { payload }) => ({
  ...state,
  quoteConversationMap: {
    ...state.quoteConversationMap,
    [payload.quoteId]: payload.conversationId,
  },
});

export default function conversationsReducer(state = initialState, action) {
  switch (action.type) {
    case CONVERSATION_CREATED:
      return onCreateConversation(state, action);
    case CONVERSATION_READ_ONE:
      return onReadOneConversation(state, action);
    case CONVERSATION_READ_ALL:
      return onAllInboxConversations(state, action);
    case CONVERSATION_READ_ALL_UNREAD:
      return onAllUnreadConversations(state, action);
    case CONVERSATION_READ_ALL_ARCHIVED:
      return onAllArchivedConversations(state, action);
    case CONVERSATION_READ_ALL_SUPPLIER_V2:
      return onAllSupplierConversations(state, action);
    case MESSAGE_CREATED_V1:
    case MESSAGE_CREATED:
      return onMessageCreated(state, action);
    case MESSAGES_READ:
      return onMessagesRead(state, action);
    case MESSAGE_MARKED_READ:
      return onMessageMarkedRead(state, action);
    case UNREAD_COUNT_RECEIVED:
      return onUnreadCount(state, action);
    case ARCHIVED_COUNT_RECEIVED:
      return onArchivedCount(state, action);
    case UNREAD_COUNT_RECEIVED_QUOTES:
      return onUnreadCountQuotes(state, action);
    case TOP_MESSAGES_READ:
      return onTopMessages(state, action);
    case QUOTE_CONVERSATION_MAPPED:
      return onQuoteConversationMapped(state, action);
    case RESET_MESSAGES:
      return initialState;
    default:
      return state;
  }
}
